import { Close } from "@mui/icons-material";
import { Button, ButtonProps, IconButton, Menu, MenuItem, MenuProps } from "@mui/material";
import { Fragment, ReactNode, useCallback, useState } from "react";

export type MenuSelectProps<T> = {
  menuProps?: MenuProps;
  maxHeight?: number | string;
  buttonProps?: ButtonProps;
  disableClearable?: boolean;
  isOptionEqualTo?: (a?: T, b?: T) => boolean;
  getOptionKey?: (option: T, index: number, list: T[]) => string;
  renderItem: (option: T, index: number, list: T[]) => ReactNode;
  options?: T[];
  children: ReactNode | ((handleClick: (event: React.MouseEvent<HTMLElement>) => void) => ReactNode);
} & (
  | { multiple?: false; value?: T; onChange?: (option?: T) => void }
  | { multiple: true; value?: T[]; onChange?: (options: T[]) => void }
);

export default function MenuSelect<T>({
  maxHeight = 48 * 4.5,
  options,
  value,
  multiple,
  menuProps,
  children,
  buttonProps,
  disableClearable,
  onChange,
  renderItem,
  getOptionKey,
  isOptionEqualTo = (a, b) => a === b // default comparator
}: MenuSelectProps<T>) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const isSelected = multiple ? value && value?.length > 0 : value;

  const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleOptionClick = useCallback(
    (option: T) => () => {
      if (!onChange) return;

      if (multiple) {
        const current = (value as T[]) || [];
        const exists = current.some((item) => isOptionEqualTo(item, option));

        const updated = exists ? current.filter((item) => !isOptionEqualTo(item, option)) : [...current, option];
        if (disableClearable && updated.length <= 0) {
          return;
        }
        onChange(updated);
      } else {
        if (disableClearable) {
          onChange(option);
        } else {
          onChange(isOptionEqualTo(option, value as T) ? undefined : option);
        }
        handleClose();
      }
    },
    [multiple, onChange, value, disableClearable, handleClose, isOptionEqualTo]
  );

  return (
    <Fragment>
      {typeof children === "function" ? (
        children(handleClick)
      ) : (
        <Button
          color="secondary"
          size="large"
          variant={isSelected ? "contained" : "outlined"}
          onClick={handleClick}
          endIcon={
            isSelected &&
            !disableClearable && (
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  if (onChange) {
                    if (multiple) {
                      onChange([]);
                    } else {
                      onChange(undefined);
                    }
                  }
                }}
                color="inherit"
                sx={{ p: 0 }}
              >
                <Close />
              </IconButton>
            )
          }
          {...buttonProps}
        >
          {children}
        </Button>
      )}

      <Menu
        id="menu-select"
        MenuListProps={{ "aria-labelledby": "menu-select-trigger" }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            style: { maxHeight, width: "auto" }
          }
        }}
        {...menuProps}
      >
        {options?.map((option, index, arr) => {
          const selected = multiple
            ? (value as T[])?.some((item) => isOptionEqualTo(item, option))
            : isOptionEqualTo(option, value as T);

          return (
            <MenuItem
              key={getOptionKey ? getOptionKey(option, index, arr) : `${index}`}
              selected={selected}
              onClick={handleOptionClick(option)}
            >
              {renderItem(option, index, arr)}
            </MenuItem>
          );
        })}
      </Menu>
    </Fragment>
  );
}
